import React from 'react';
import styled from 'styled-components';
import { LocationPlaces, DeliveryType } from '../shared';
import { ReactComponent as ExpressIcon } from 'assets/icons/delivery-express.svg';
import { ReactComponent as StandardIcon } from 'assets/icons/delivery-standard.svg';
import { ReactComponent as EconomyIcon } from 'assets/icons/delivery-economy.svg';
import { formatDate } from 'utils/date';
import { formatPrice } from 'utils/price';
import { Text } from 'components/Text';
import { Error } from 'components/Error';
import { RadioList, RadioListItem } from 'components/RadioList';
import { useDeliveryValues } from '../useDeliveryValues';

type Props = {
  volume: number;
  location: Required<LocationPlaces>;
  pickupDate?: Date;
  delivery?: DeliveryType;
  onDeliveryChange: (type: DeliveryType) => void;
};

export const Delivery = (props: Props) => {
  const {
    volume,
    location,
    pickupDate,
    delivery,
    onDeliveryChange = () => {},
  } = props;

  const [error, setError] = React.useState<string>();
  const { dates, prices } = useDeliveryValues({
    volume,
    location,
    pickupDate,
  });

  function handleDeliveryTypeChange(
    event: React.ChangeEvent<HTMLInputElement>
  ) {
    const radio = event.currentTarget as HTMLInputElement;
    const type = radio.value as DeliveryType;
    onDeliveryChange(type);
  }

  React.useEffect(() => {
    setError(!dates || !prices ? 'LOCATION_UNMATCHED' : undefined);
  }, [dates, prices]);

  return (
    <RadioList>
      {error === 'LOCATION_UNMATCHED' && (
        <Error>
          Sorry, we are currently unable to complete your order online for your
          chosen locations. Please contact us to place your order.
        </Error>
      )}
      <RadioListItem
        value="economy"
        checked={delivery === 'economy'}
        onChange={handleDeliveryTypeChange}
        disabled={error}
      >
        <EconomyIcon />
        <DeliveryText weight="medium">Economy</DeliveryText>
        <DeliveryText weight="light" opacity={0.5}>
          Delivered by {dates?.economy ? formatDate(dates.economy) : '-'}
        </DeliveryText>
        <DeliveryText font="xlarge">
          &euro;
          {prices?.economy ? formatPrice(prices.economy) : '-'}
        </DeliveryText>
      </RadioListItem>

      <RadioListItem
        value="standard"
        checked={delivery === 'standard'}
        onChange={handleDeliveryTypeChange}
        disabled={error}
      >
        <StandardIcon />
        <DeliveryText weight="medium">Standard</DeliveryText>
        <DeliveryText weight="light" opacity={0.5}>
          Delivered by {dates?.standard ? formatDate(dates.standard) : '-'}
        </DeliveryText>
        <DeliveryText font="xlarge">
          &euro;
          {prices?.standard ? formatPrice(prices.standard) : '-'}
        </DeliveryText>
      </RadioListItem>
      <RadioListItem
        value="express"
        checked={delivery === 'express'}
        onChange={handleDeliveryTypeChange}
        disabled={error}
      >
        <ExpressIcon />
        <DeliveryText weight="medium">Express</DeliveryText>
        <DeliveryText weight="light" opacity={0.5}>
          Delivered by {dates?.express ? formatDate(dates.express) : '-'}
        </DeliveryText>
        <DeliveryText font="xlarge">
          &euro;
          {prices?.express ? formatPrice(prices.express) : '-'}
        </DeliveryText>
      </RadioListItem>
    </RadioList>
  );
};

const DeliveryText = styled(Text).attrs(() => ({
  as: 'p',
}))`
  margin-top: ${props => props.theme.spacing.small};
`;
